import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { OpenRouterResult } from '../../utils/api';
import { cca2Of, countryNames } from '../../data/geography';
import { formatInt } from '../../utils/format';
import { AccessFilter, orCountry, orIsOpenFamily, orMatches, orPrefix } from '../../utils/modelFilters';
import { OPENROUTER_API } from '../../utils/sourceIndex';
import { useCappedPages } from '../../hooks/useCappedPages';
import { DataBadge } from '../DataBadge';
import { SourceRef } from '../SourceRef';
import { PageSources } from '../PageSources';
import { Pager } from '../Pager';
interface OpenRouterTableProps {
  result: OpenRouterResult | null;
  access: AccessFilter;
  country: string;
}
export function OpenRouterTable({
  result,
  access,
  country
}: OpenRouterTableProps) {
  const rows = useMemo(() => {
    const list = (result?.models ?? []).filter(m => orMatches(m, access) && (country === 'all' || orCountry(m) === country));
    return [...list].sort((a, b) => (b.created ?? 0) - (a.created ?? 0));
  }, [result, access, country]);
  const pager = useCappedPages(rows);
  return <section aria-labelledby="or-title" data-id="element-610" data-mp="190ofcvgbyaym">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-611" data-mp="18xk2zaspo8wv">
        <div data-id="element-612" data-mp="1dxewlm3vfclm">
          <h2 id="or-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-613" data-mp="1dwwo9r3xp7xc">
            Every model you can call today
            <SourceRef href={OPENROUTER_API} label="OpenRouter models API" grade="A" data-id="element-614" data-mp="1nosxnybh0kgv" />
          </h2>
          <p className="mt-1 max-w-2xl text-[13.5px] text-muted" data-id="element-615" data-mp="1rq4hfn7nybh5">
            {result ? formatInt(result.models.length) : '…'} models served through OpenRouter, newest first, with the publisher’s home country.
          </p>
        </div>
      </div>
      <PageSources data-id="element-616" data-mp="21o24469vk07n">
        <DataBadge mode={result ? result.mode : 'loading'} source="OpenRouter · model catalog" asOf={result?.asOf} data-id="element-617" data-mp="21mhkvct0g3p3" />
      </PageSources>
      <div className="mt-5 overflow-x-auto rounded-lg border border-line" data-id="element-618" data-mp="7ab1hqpyqglp">
        <table className="w-full min-w-[920px] border-collapse text-[13.5px]" data-id="element-619" data-mp="7cdvsmy5evw8">
          <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-620" data-mp="3vfiz3wpoynup">
            <tr data-id="element-621" data-mp="3rez1yt54bk8k">
              {['Model', 'Publisher', 'Country', 'Context', 'Price', 'Added'].map(h => <th key={h} scope="col" className="whitespace-nowrap border-b border-line px-3 py-2.5 font-normal" data-id="element-622" data-mp="99m0n33ik4u0">
                  {h}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-623" data-mp="i6wfeyfjic3o">
            {!result && <tr data-id="element-624" data-mp="e7nk3p4uq660">
                <td colSpan={6} className="px-3 py-12 text-center font-mono text-xs text-muted" role="status" data-id="element-625" data-mp="e62r43y6qva9">
                  Fetching OpenRouter catalog…
                </td>
              </tr>}
            {pager.visible.map(m => {
            const cc = orCountry(m);
            return <tr key={m.id} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-626" data-mp="11x850dkkfdhx">
                  <td className="px-3 py-2" data-id="element-627" data-mp="wz6mbtr1xg6z">
                    <a href={`https://openrouter.ai/${m.id.replace(/:free$/, '')}`} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-machine" data-id="element-628" data-mp="1gjoowfrb879f">
                      {m.name}
                    </a>
                    {orIsOpenFamily(m) && <span className="ml-2 font-mono text-[10px] text-live" data-id="element-629" data-mp="16kfzm07e3gte">open weights</span>}
                    <p className="font-mono text-[10.5px] text-faint" data-id="element-630" data-mp="1bj1080qk02id">{m.id}</p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px] text-muted" data-id="element-631" data-mp="wumot1f2n5q7">{orPrefix(m)}</td>
                  <td className="whitespace-nowrap px-3 py-2" data-id="element-632" data-mp="11npmnlqvt1c7">
                    {cc ? <Link to={`/countries/${cc}`} className="inline-flex items-center gap-2 text-muted hover:text-machine" data-id="element-633" data-mp="nwazr3tusfzv">
                        {cca2Of[cc] && <img src={`https://flagcdn.com/${cca2Of[cc]}.svg`} alt="" loading="lazy" className="h-3 w-4 rounded-[2px] object-cover ring-1 ring-line" data-id="element-634" data-mp="dwk24t9zxkvk" />}
                        {countryNames[cc] ?? cc}
                      </Link> : <span className="text-faint" data-id="element-635" data-mp="1m5rbbasx51gm">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[12.5px] text-ink" data-id="element-636" data-mp="1h89ay8syvuk8">{m.context ? `${formatInt(m.context / 1000)}K` : '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2" data-id="element-637" data-mp="17ig23fk7ltjo">
                    <span className={`font-mono text-[11px] ${m.free ? 'text-live' : 'text-muted'}`} data-id="element-638" data-mp="17fttdcll17zv">{m.free ? 'Free ($0)' : 'Paid'}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px] text-muted" data-id="element-639" data-mp="yg6pmk5drq3n">{m.created ? new Date(m.created * 1000).toISOString().slice(0, 10) : '—'}</td>
                </tr>;
          })}
            {result && rows.length === 0 && <tr data-id="element-640" data-mp="2nwfw69wdioz0">
                <td colSpan={6} className="px-3 py-10 text-center text-muted" data-id="element-641" data-mp="2nuld714nx6xk">
                  No live models match these filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      <Pager page={pager.page} pages={pager.pages} setPage={pager.setPage} total={pager.total} isCapped={pager.isCapped} data-id="element-642" data-mp="2qg2mhd2ded7c" />
    </section>;
}